import { supabase, isConfigured } from './supabase';

// ============================================================
// PLANOS ANALYTICS SERVICE — lightweight project event tracking
// ============================================================

export type PlanosEventType = 'view' | 'hotspot_click' | 'video_play' | 'map_open' | 'download_click';

const getVisitorId = (): string => {
  const KEY = '__planos_visitor_id__';
  let id = sessionStorage.getItem(KEY);
  if (!id) {
    const raw = `${Date.now()}-${Math.random().toString(36).slice(2)}-${navigator.userAgent}`;
    let hash = 0;
    for (let i = 0; i < raw.length; i++) {
      const c = raw.charCodeAt(i);
      hash = ((hash << 5) - hash) + c;
      hash |= 0;
    }
    id = Math.abs(hash).toString(36);
    sessionStorage.setItem(KEY, id);
  }
  return id;
};

/** Track an analytics event — fire-and-forget. */
export const trackEvent = (
  projectId: string,
  eventType: PlanosEventType,
  metadata?: { hotspotId?: string }
): void => {
  if (!isConfigured) return;

  supabase
    .from('planos_analytics')
    .insert({
      project_id: projectId,
      event_type: eventType,
      hotspot_id: metadata?.hotspotId ?? null,
      visitor_id: getVisitorId(),
      referrer: document.referrer || null,
      user_agent: navigator.userAgent.slice(0, 256),
    })
    .then(({ error }) => {
      if (error) console.warn('[Planos Analytics] Insert failed:', error.message);
    });

  if (eventType === 'view') {
    supabase.rpc('increment_planos_views', { p_project_id: projectId }).then(({ error }) => {
      if (error) console.warn('[Planos Analytics] Increment views failed:', error.message);
    });
  }
};

export interface PlanosAnalyticsSummary {
  totalViews: number;
  uniqueVisitors: number;
  totalHotspotClicks: number;
  viewsByDay: { date: string; count: number }[];
  popularHotspots: { hotspot_id: string; count: number }[];
  avgTimePerView: number;
}

export const getAnalyticsSummary = async (projectId: string): Promise<PlanosAnalyticsSummary | null> => {
  if (!isConfigured) return null;

  try {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const { data: events, error } = await supabase
      .from('planos_analytics')
      .select('event_type, hotspot_id, visitor_id, created_at')
      .eq('project_id', projectId)
      .gte('created_at', thirtyDaysAgo.toISOString())
      .order('created_at', { ascending: false })
      .limit(5000);

    if (error) {
      console.warn('[Planos Analytics] Summary fetch error:', error.message);
      return {
        totalViews: 0,
        uniqueVisitors: 0,
        totalHotspotClicks: 0,
        viewsByDay: [],
        popularHotspots: [],
        avgTimePerView: 0,
      };
    }

    if (!events || events.length === 0) {
      return {
        totalViews: 0,
        uniqueVisitors: 0,
        totalHotspotClicks: 0,
        viewsByDay: [],
        popularHotspots: [],
        avgTimePerView: 0,
      };
    }

    const views = events.filter((e) => e.event_type === 'view');
    const hotspotClicks = events.filter((e) => e.event_type === 'hotspot_click');
    const uniqueVisitors = new Set(events.map((e) => e.visitor_id).filter(Boolean)).size;

    const dayMap = new Map<string, number>();
    for (let i = 29; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      dayMap.set(d.toISOString().slice(0, 10), 0);
    }
    for (const e of views) {
      const day = e.created_at.slice(0, 10);
      dayMap.set(day, (dayMap.get(day) || 0) + 1);
    }
    const viewsByDay = Array.from(dayMap.entries()).map(([date, count]) => ({ date, count }));

    const hotspotCountMap = new Map<string, number>();
    for (const e of hotspotClicks) {
      if (e.hotspot_id) {
        hotspotCountMap.set(e.hotspot_id, (hotspotCountMap.get(e.hotspot_id) || 0) + 1);
      }
    }
    const popularHotspots = Array.from(hotspotCountMap.entries())
      .map(([hotspot_id, count]) => ({ hotspot_id, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    const visitorTimes = new Map<string, string[]>();
    for (const e of events) {
      if (e.visitor_id) {
        if (!visitorTimes.has(e.visitor_id)) visitorTimes.set(e.visitor_id, []);
        visitorTimes.get(e.visitor_id)!.push(e.created_at);
      }
    }
    let totalTime = 0;
    let count = 0;
    for (const times of visitorTimes.values()) {
      if (times.length >= 2) {
        const first = new Date(times[times.length - 1]).getTime();
        const last = new Date(times[0]).getTime();
        totalTime += (last - first) / 1000;
        count++;
      }
    }
    const avgTimePerView = count > 0 ? Math.round(totalTime / count) : 0;

    return {
      totalViews: views.length,
      uniqueVisitors,
      totalHotspotClicks: hotspotClicks.length,
      viewsByDay,
      popularHotspots,
      avgTimePerView,
    };
  } catch (err) {
    console.warn('[Planos Analytics] Summary error:', err);
    return {
      totalViews: 0,
      uniqueVisitors: 0,
      totalHotspotClicks: 0,
      viewsByDay: [],
      popularHotspots: [],
      avgTimePerView: 0,
    };
  }
};
